import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { CloudNavBar } from "../components/CloudNavBar";
import { StatusPageServiceManager } from "../components/StatusPageServiceManager";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@ensafely/checker-ui";

// ─── Types ───────────────────────────────────────────────────────────────────

interface StatusPageConfig {
  tenant_id: string;
  enabled: boolean;
  slug: string;
  title: string;
  description: string;
  logo_url: string;
  created_at: string;
  updated_at: string;
  status_page_base_url?: string;
}

interface StatusPageService {
  id: string;
  tenant_id: string;
  check_uuid: string;
  display_name: string;
  sort_order: number;
  created_at: string;
}

interface ServiceQuota {
  current: number;
  limit: number;
}

interface ServicesResponse {
  services: StatusPageService[];
  quota: ServiceQuota;
}

interface IncidentUpdate {
  id: string;
  incident_id: string;
  status: string;
  message: string;
  created_at: string;
}

interface Incident {
  id: string;
  tenant_id: string;
  title: string;
  status: string;
  auto_generated: boolean;
  updates: IncidentUpdate[];
  services: string[];
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

interface CheckDefinition {
  uuid: string;
  name: string;
  project: string;
  group_name: string;
  type: string;
  url?: string;
  host?: string;
  domain?: string;
  [key: string]: unknown;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function incidentStatusBadge(status: string) {
  const map: Record<string, string> = {
    investigating: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
    identified: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400",
    monitoring: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
    resolved: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  };
  return map[status] ?? "bg-muted text-muted-foreground";
}

function slugValid(slug: string): boolean {
  return /^[a-z0-9-]{3,60}$/.test(slug);
}

// ─── Create Incident Modal ────────────────────────────────────────────────────

interface CreateIncidentModalProps {
  services: StatusPageService[];
  onSubmit: (title: string, status: string, message: string, serviceIds: string[]) => Promise<void>;
  onClose: () => void;
}

function CreateIncidentModal({ services, onSubmit, onClose }: CreateIncidentModalProps) {
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("investigating");
  const [message, setMessage] = useState("");
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggleService = (id: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError("");
    try {
      await onSubmit(title.trim(), status, message.trim(), selectedServiceIds);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create incident");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Incident</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <label className="block">
            <span className="block text-sm font-medium mb-1">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. API response times degraded"
              required
              className="w-full border rounded px-3 py-2 text-sm bg-background"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium mb-1">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm bg-background"
            >
              <option value="investigating">Investigating</option>
              <option value="identified">Identified</option>
              <option value="monitoring">Monitoring</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium mb-1">Initial Message</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe what is happening..."
              rows={3}
              className="w-full border rounded px-3 py-2 text-sm bg-background resize-none"
            />
          </label>
          {services.length > 0 && (
            <div>
              <span className="block text-sm font-medium mb-2">Affected Services</span>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {services.map((svc) => (
                  <label key={svc.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedServiceIds.includes(svc.id)}
                      onChange={() => toggleService(svc.id)}
                      className="rounded"
                    />
                    {svc.display_name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded border hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Creating..." : "Create Incident"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Incident Row ─────────────────────────────────────────────────────────────

interface IncidentRowProps {
  incident: Incident;
  services: StatusPageService[];
  onUpdate: (incidentId: string, status: string, message: string) => Promise<void>;
  onResolve: (incidentId: string) => Promise<void>;
  onDelete: (incidentId: string) => Promise<void>;
}

function IncidentRow({ incident, services, onUpdate, onResolve, onDelete }: IncidentRowProps) {
  const [updateStatus, setUpdateStatus] = useState("monitoring");
  const [updateMessage, setUpdateMessage] = useState("");
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [savingResolve, setSavingResolve] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [updateError, setUpdateError] = useState("");

  const affectedServices = services.filter((s) => incident.services?.includes(s.id));

  const handleAddUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingUpdate(true);
    setUpdateError("");
    try {
      await onUpdate(incident.id, updateStatus, updateMessage);
      setUpdateMessage("");
      setShowUpdateForm(false);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : "Failed to post update");
    } finally {
      setSavingUpdate(false);
    }
  };

  const handleResolve = async () => {
    setSavingResolve(true);
    try {
      await onResolve(incident.id);
    } finally {
      setSavingResolve(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow p-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{incident.title}</span>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded capitalize ${incidentStatusBadge(incident.status)}`}
            >
              {incident.status}
            </span>
            {incident.auto_generated && (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400">
                Auto
              </span>
            )}
          </div>
          {affectedServices.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Affects: {affectedServices.map((s) => s.display_name).join(", ")}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date(incident.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowUpdateForm((v) => !v)}
            className="text-xs px-2 py-1 rounded border hover:bg-muted transition-colors"
          >
            Add Update
          </button>
          {incident.status !== "resolved" && (
            <button
              onClick={handleResolve}
              disabled={savingResolve}
              className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {savingResolve ? "..." : "Resolve"}
            </button>
          )}
          {confirmDelete ? (
            <span className="flex items-center gap-1">
              <button
                onClick={() => onDelete(incident.id)}
                className="text-xs text-red-600 hover:underline font-medium"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-xs text-muted-foreground hover:underline"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-red-500 hover:text-red-700"
              title="Delete incident"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Updates list */}
      {incident.updates && incident.updates.length > 0 && (
        <div className="mt-3 space-y-2 border-t pt-3">
          {incident.updates.map((u) => (
            <div key={u.id} className="text-xs">
              <span
                className={`inline-block font-medium px-1.5 py-0.5 rounded capitalize mr-2 ${incidentStatusBadge(u.status)}`}
              >
                {u.status}
              </span>
              <span className="text-muted-foreground">{new Date(u.created_at).toLocaleString()}</span>
              {u.message && <p className="mt-1 text-foreground">{u.message}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Add Update form */}
      {showUpdateForm && (
        <form onSubmit={handleAddUpdate} className="mt-3 border-t pt-3 space-y-2">
          {updateError && <p className="text-red-600 text-xs">{updateError}</p>}
          <div className="flex gap-2">
            <select
              value={updateStatus}
              onChange={(e) => setUpdateStatus(e.target.value)}
              className="border rounded px-2 py-1 text-xs bg-background"
            >
              <option value="investigating">Investigating</option>
              <option value="identified">Identified</option>
              <option value="monitoring">Monitoring</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <textarea
            value={updateMessage}
            onChange={(e) => setUpdateMessage(e.target.value)}
            placeholder="Update message..."
            rows={2}
            required
            className="w-full border rounded px-2 py-1 text-xs bg-background resize-none"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={savingUpdate || !updateMessage.trim()}
              className="px-3 py-1 text-xs rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {savingUpdate ? "Posting..." : "Post Update"}
            </button>
            <button
              type="button"
              onClick={() => setShowUpdateForm(false)}
              className="px-3 py-1 text-xs rounded border hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function StatusPageConfig() {
  // Config state
  const [config, setConfig] = useState<StatusPageConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState("");
  const [configSuccess, setConfigSuccess] = useState("");

  // Config form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [slug, setSlug] = useState("");
  const [enabled, setEnabled] = useState(false);

  // Services state
  const [services, setServices] = useState<StatusPageService[]>([]);
  const [quota, setQuota] = useState<ServiceQuota>({ current: 0, limit: 0 });
  const [servicesLoading, setServicesLoading] = useState(true);

  // Incidents state
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [incidentsLoading, setIncidentsLoading] = useState(true);
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const [incidentError, setIncidentError] = useState("");

  // Check definitions
  const [checkDefs, setCheckDefs] = useState<CheckDefinition[]>([]);

  // ── Load data ──────────────────────────────────────────────────────────────

  const loadConfig = useCallback(() => {
    setConfigLoading(true);
    api
      .get<StatusPageConfig>("/status-page/config")
      .then((r) => {
        setConfig(r);
        setTitle(r.title || "");
        setDescription(r.description || "");
        setLogoUrl(r.logo_url || "");
        setSlug(r.slug || "");
        setEnabled(r.enabled || false);
      })
      .catch(() => {})
      .finally(() => setConfigLoading(false));
  }, []);

  const loadServices = useCallback(() => {
    setServicesLoading(true);
    api
      .get<ServicesResponse>("/status-page/services")
      .then((r) => {
        const sorted = (r.services || []).slice().sort((a, b) => a.sort_order - b.sort_order);
        setServices(sorted);
        setQuota(r.quota || { current: 0, limit: 0 });
      })
      .catch(() => {})
      .finally(() => setServicesLoading(false));
  }, []);

  const loadIncidents = useCallback(() => {
    setIncidentsLoading(true);
    api
      .get<{ incidents: Incident[] }>("/status-page/incidents")
      .then((r) => {
        setIncidents(r.incidents || []);
      })
      .catch(() => {})
      .finally(() => setIncidentsLoading(false));
  }, []);

  const loadCheckDefs = useCallback(() => {
    api
      .get<CheckDefinition[]>("/check-definitions")
      .then((r) => setCheckDefs(Array.isArray(r) ? r : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadConfig();
    loadServices();
    loadIncidents();
    loadCheckDefs();
  }, [loadConfig, loadServices, loadIncidents, loadCheckDefs]);

  // ── Config save ────────────────────────────────────────────────────────────

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (slug && !slugValid(slug)) {
      setConfigError("Slug must be 3–60 lowercase alphanumeric characters or hyphens.");
      return;
    }
    setConfigSaving(true);
    setConfigError("");
    setConfigSuccess("");
    try {
      const updated = await api.put<StatusPageConfig>("/status-page/config", {
        title,
        description,
        logo_url: logoUrl,
        enabled,
        slug,
      });
      setConfig(updated);
      setConfigSuccess("Configuration saved.");
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : "Failed to save configuration");
    } finally {
      setConfigSaving(false);
    }
  };

  // ── Incidents ──────────────────────────────────────────────────────────────

  const handleCreateIncident = async (
    incTitle: string,
    status: string,
    message: string,
    serviceIds: string[]
  ) => {
    await api.post("/status-page/incidents", {
      title: incTitle,
      status,
      message,
      service_ids: serviceIds,
    });
    loadIncidents();
  };

  const handleAddIncidentUpdate = async (incidentId: string, status: string, message: string) => {
    await api.post(`/status-page/incidents/${incidentId}/updates`, { status, message });
    loadIncidents();
  };

  const handleResolveIncident = async (incidentId: string) => {
    setIncidentError("");
    try {
      await api.put(`/status-page/incidents/${incidentId}`, { status: "resolved" });
      loadIncidents();
    } catch (err) {
      setIncidentError(err instanceof Error ? err.message : "Failed to resolve incident");
    }
  };

  const handleDeleteIncident = async (incidentId: string) => {
    setIncidentError("");
    try {
      await api.del(`/status-page/incidents/${incidentId}`);
      loadIncidents();
    } catch (err) {
      setIncidentError(err instanceof Error ? err.message : "Failed to delete incident");
    }
  };

  // ── URL helpers ─────────────────────────────────────────────────────────────

  const statusPageBaseUrl = config?.status_page_base_url;
  const publicUrl = config?.slug
    ? statusPageBaseUrl
      ? `${statusPageBaseUrl}/${config.slug}`
      : `${window.location.origin}/s/${config.slug}`
    : null;
  const canPreview = config?.enabled && publicUrl;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />

      {showCreateIncident && (
        <CreateIncidentModal
          services={services}
          onSubmit={handleCreateIncident}
          onClose={() => setShowCreateIncident(false)}
        />
      )}

      <div className="max-w-3xl mx-auto py-8 px-6">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h1 className="text-2xl font-bold">Status Page</h1>
          {canPreview && (
            <a
              href={publicUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border hover:bg-muted transition-colors"
            >
              Preview Status Page
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>

        {/* ── Configuration Section ── */}
        <section className="bg-card rounded-lg shadow p-6 mb-6">
          <h2 className="text-base font-semibold mb-4">Configuration</h2>
          {configLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <form onSubmit={handleSaveConfig} className="space-y-4">
              {configError && <p className="text-red-600 text-sm">{configError}</p>}
              {configSuccess && <p className="text-green-600 text-sm">{configSuccess}</p>}

              <label className="block">
                <span className="block text-sm font-medium mb-1">Title</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Acme Status"
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium mb-1">Description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Current system status and incident history."
                  rows={2}
                  className="w-full border rounded px-3 py-2 text-sm bg-background resize-none"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium mb-1">Logo URL</span>
                <input
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium mb-1">Slug</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  placeholder="acme-status"
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  3–60 lowercase letters, numbers, and hyphens. Public URL: <code>{statusPageBaseUrl ? `${statusPageBaseUrl}/${slug || "<slug>"}` : `${window.location.origin}/s/${slug || "<slug>"}`}</code>
                </p>
                {slug && !slugValid(slug) && (
                  <p className="text-xs text-red-600 mt-0.5">
                    Invalid slug format.
                  </p>
                )}
              </label>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  onClick={() => setEnabled((v) => !v)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    enabled ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      enabled ? "translate-x-4" : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="text-sm">{enabled ? "Public page enabled" : "Public page disabled"}</span>
              </div>

              {enabled && publicUrl && (
                <p className="text-xs text-muted-foreground">
                  Public page:{" "}
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {publicUrl}
                  </a>
                </p>
              )}

              <div>
                <button
                  type="submit"
                  disabled={configSaving}
                  className="bg-primary text-primary-foreground rounded px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                >
                  {configSaving ? "Saving..." : "Save Configuration"}
                </button>
              </div>
            </form>
          )}
        </section>

        {/* ── Services Section ── */}
        <section className="bg-card rounded-lg shadow p-6 mb-6">
          <h2 className="text-base font-semibold mb-4">Services</h2>
          {servicesLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <StatusPageServiceManager
              checkDefs={checkDefs}
              services={services}
              quota={quota}
              onServicesChanged={loadServices}
            />
          )}
        </section>

        {/* ── Incidents Section ── */}
        <section className="bg-card rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h2 className="text-base font-semibold">Incidents</h2>
            <button
              onClick={() => setShowCreateIncident(true)}
              className="bg-primary text-primary-foreground rounded px-3 py-1.5 text-sm hover:opacity-90"
            >
              Create Incident
            </button>
          </div>

          {incidentError && <p className="text-red-600 text-sm mb-3">{incidentError}</p>}

          {incidentsLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : incidents.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No incidents. All systems operational.
            </p>
          ) : (
            <>
              {/* Active incidents */}
              {incidents.filter((i) => i.status !== "resolved").length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Active</h3>
                  <div className="space-y-3">
                    {incidents
                      .filter((i) => i.status !== "resolved")
                      .map((incident) => (
                        <IncidentRow
                          key={incident.id}
                          incident={incident}
                          services={services}
                          onUpdate={handleAddIncidentUpdate}
                          onResolve={handleResolveIncident}
                          onDelete={handleDeleteIncident}
                        />
                      ))}
                  </div>
                </div>
              )}
              {/* Resolved incidents */}
              {incidents.filter((i) => i.status === "resolved").length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Recently Resolved</h3>
                  <div className="space-y-3">
                    {incidents
                      .filter((i) => i.status === "resolved")
                      .map((incident) => (
                        <IncidentRow
                          key={incident.id}
                          incident={incident}
                          services={services}
                          onUpdate={handleAddIncidentUpdate}
                          onResolve={handleResolveIncident}
                          onDelete={handleDeleteIncident}
                        />
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
